import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import range from 'lodash/range';
import {
  useGenerateOrderMutation,
  useGenerateRegisterMutation,
} from '@/store/checkout/checkout.api';
import useCallApiProcess from '../useCallApiProcess';
import {
  useBooking,
  useSeatsIO,
  useRsvp,
  useExchange,
  useSaturated,
  useEvent,
} from '@/store';
import useAnalyticsEvents from '@/hooks/useAnalyticsEvents';
import useTicketsContext from '@/contexts/tickets/useTicketsContext';
import useStepper from '@/hooks/checkout/useStepper';
import useCustomLinkActions from '@/store/custom-links/useCustomLinksActions';
import useMerch from '../useMerch';
import { eventHasMerch, ticketHasMerch } from '@/config/onDemandMerchConfig';

export default function useCheckoutGenerateOrder() {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const router = useRouter();
  const {
    event: { id, use_seats, minutes_count_down },
    preferences,
  } = useSelector((state) => state.event || { event: {} });
  const {
    seatsIO: { holdToken, expiresAt },
  } = useSeatsIO();
  const ticketsSelected = useSelector((state) =>
    state.ticketsSelected.ids.map((id) => state.ticketsSelected.entities[id]),
  );
  const { setBookingOrder, setBookingId, setRegisterId, setStartBookingDate } =
    useBooking();
  const { nextStep } = useStepper();
  const { setExchangeToken } = useExchange();
  const { buyTickets, addToCart } = useAnalyticsEvents();
  const { isActive: isActiveRsvp } = useRsvp();
  const {
    customLink: { link },
  } = useCustomLinkActions();
  const { validateTicketsInCart } = useTicketsContext();

  const { isEnabled, decreaseTriesCounter } = useSaturated();

  const [generateOrderRequest] = useGenerateOrderMutation();
  const [generateRegisterRequest] = useGenerateRegisterMutation();

  const { callApi: callRegisterApi, alert: registerAlert } = useCallApiProcess(
    generateRegisterRequest,
    t(preferences.pre_register ? 'checkout.loadings.pleaseWait' : 'checkout.loadings.almostAlready'),
  );
  const { callApi, alert } = useCallApiProcess(
    generateOrderRequest,
    t(preferences.pre_register ? 'checkout.loadings.pleaseWait' : 'checkout.loadings.almostAlready'),
  );

  const { sendMerchData } = useMerch();

  const parseTicketType = (ticketType) => {
    const { ticket_type_id, name, price, quantity, seatsId } = ticketType;
    const result = {
      ticket_type_id,
      ticket_type_name: name,
      ticket_type_price: price,
      quantity,
    };

    if (ticketType.objectType === 'GeneralAdmissionArea') {
      result.seats = range(ticketType.quantity).map(() => seatsId);
    } else {
      result.seats = [seatsId];
    }

    return result;
  };

  const mergeTicketType = (prev, current) => {
    if (prev) {
      const result = prev;
      result.quantity += current.quantity;
      if (result.seats) {
        result.seats = [...result.seats, ...current.seats];
      }
      return result;
    }
    return current;
  };

  const getTicketsSelectedMergeByTicketType = () => {
    const ticketTypesSelected = {};
    ticketsSelected.map((ticketType) => {
      ticketTypesSelected[ticketType.ticket_type_id] = mergeTicketType(
        ticketTypesSelected[ticketType.ticket_type_id],
        parseTicketType(ticketType),
      );
    });

    return ticketTypesSelected;
  };

  const groupTicketsByName = () => {
    const ticketsByName = {};
    ticketsSelected.map((ticketType) => {
      if (ticketsByName[ticketType.name]) {
        ticketsByName[ticketType.name] = {
          ...ticketType,
          quantity:
            ticketsByName[ticketType.name].quantity + ticketType.quantity,
        };
      } else {
        ticketsByName[ticketType.name] = ticketType;
      }
    });
    return Object.values(ticketsByName);
  };

  const checkMinMaxForTicket = () => {
    let tickets = ticketsSelected;
    if (use_seats) {
      tickets = groupTicketsByName();
    }
    const item = tickets.find(
      (ticket) =>
        ticket.quantity < ticket.min ||
        ticket.quantity > ticket.max ||
        ticket.quantity > ticket.availability,
    );
    if (item) {
      if (item.quantity < item.min) {
        return t('checkout.home.alerts.minTickets', {
          count: item.min,
          name: item.name,
        });
      }
      if (item.quantity > item.max) {
        return t('checkout.home.alerts.maxTickets', {
          count: item.max,
          name: item.name,
        });
      }
      if (item.quantity > item.availability) {
        return t('checkout.home.alerts.sorryNoTickets', {
          name: item.name,
        });
      }
    }
    return '';
  };

  const generateOrder = async (token, recaptchaToken) => {
    if (isLoading) return;

    if (router.pathname === '/' && (await validateTicketsInCart())) {
      setIsLoading(true);
      const checkedTickets = checkMinMaxForTicket();
      if (checkedTickets) {
        setIsLoading(false);
        return alert(checkedTickets);
      }

      if (token) {
        const tickets = getTicketsSelectedMergeByTicketType();
        let body = {
          event_id: id,
          body_req: Object.values(tickets),
          minutes_count_down,
        };
        if (use_seats) {
          body = {
            ...body,
            ...(isActiveRsvp
              ? {}
              : {
                hold_token: holdToken,
                seats_token_expire: expiresAt,
              }),
          };
        }
        const response = await callApi({ body, link, recaptchaToken });

        if (response.data) {
          // Guardar exchange_token de la respuesta
          if (response.data.exchange_token) {
            setExchangeToken(response.data.exchange_token);
          }

          if (isEnabled) {
            decreaseTriesCounter();
          }

          buyTickets(body, response.data);
          sendMerchData(ticketsSelected);
          addToCart();
          setBookingOrder(response.data.booking_number);
          setBookingId(response.data.booking_id);
          setStartBookingDate();
          nextStep();
        }
        setIsLoading(false);
      } else {
        setIsLoading(false);
        return alert(t('checkout.home.alerts.recaptchaError'));
      }
    }
  };

  const generateRegister = async (token, recaptchaToken, ticketType) => {
    if (isLoading) return;

    if (router.pathname === '/') {
      setIsLoading(true);

      if (token) {
        let body = {
          event_id: id,
        };

        const response = await callRegisterApi({
          body,
          link,
          recaptchaToken,
          ticketType,
        });

        if (response.data) {
          setRegisterId(response.data.id);
          // GuardaO exchange_token de la respuesta
          if (response.data.exchange_token) {
            setExchangeToken(response.data.exchange_token);
          }

          nextStep();
        }
        setIsLoading(false);
      } else {
        setIsLoading(false);
        return alert(t('checkout.home.alerts.recaptchaError'));
      }
    }
  };

  return {
    generateOrder,
    generateRegister,
    isLoading,
  };
}
